// routes
import { PATH_DASHBOARD } from '../../../routes/paths';
// components
import SvgIconStyle from '../../../components/SvgIconStyle';
import LeadsNavBadge from './LeadsNavBadge';

// ----------------------------------------------------------------------

const getIcon = (name: string) => (
  <SvgIconStyle src={`/assets/icons/navbar/${name}.svg`} sx={{ width: 1, height: 1 }} />
);

const ICONS = {
  blog: getIcon('ic_blog'),
  cart: getIcon('ic_cart'),
  chat: getIcon('ic_chat'),
  mail: getIcon('ic_mail'),
  user: getIcon('ic_user'),
  kanban: getIcon('ic_kanban'),
  banking: getIcon('ic_banking'),
  booking: getIcon('ic_booking'),
  invoice: getIcon('ic_invoice'),
  calendar: getIcon('ic_calendar'),
  ecommerce: getIcon('ic_ecommerce'),
  analytics: getIcon('ic_analytics'),
  dashboard: getIcon('ic_dashboard'),
  menuItem: getIcon('ic_menu_item'),
  messages: getIcon('ic_chat'),
};

const navConfig = [
  // GENERAL
  // ----------------------------------------------------------------------
  {
    subheader: 'general',
    roles: ['admin', 'super-admin', 'sales-manager'],
    items: [
      {
        title: 'app',
        path: PATH_DASHBOARD.general.app,
        icon: ICONS.dashboard,
        roles: ['admin', 'super-admin', 'sales-manager'],
      },
    ],
  },

  // Re-arranged (no merging): the old flat "management" list is split into
  // clearer sections. Every item keeps its OWN page/route + its exact `roles`,
  // so per-role visibility is unchanged — only the grouping/order differs.
  // NavSectionVertical hides a section header when the role sees none of its
  // items, so no empty "Learning"/"Commerce" headers appear for sales/cc.

  // PEOPLE
  // ----------------------------------------------------------------------
  {
    subheader: 'people',
    roles: ['admin', 'super-admin', 'customer-care', 'sales', 'sales-manager', 'support'],
    items: [
      {
        title: 'Students',
        path: PATH_DASHBOARD.user.list,
        icon: ICONS.user,
        roles: ['admin', 'super-admin', 'customer-care', 'sales', 'sales-manager', 'support'],
      },
      {
        title: 'Instructors',
        path: PATH_DASHBOARD.instructors,
        icon: ICONS.kanban,
        roles: ['admin', 'super-admin', 'customer-care', 'sales'],
      },
      {
        title: 'Messages',
        path: PATH_DASHBOARD.messages,
        icon: ICONS.messages,
        roles: ['admin', 'super-admin', 'customer-care', 'sales', 'support'],
      },
    ],
  },

  // PARTNERS (PARTNER PLATFORM S1)
  // ----------------------------------------------------------------------
  // Its own section rather than an item under People, because a partner is a
  // commercial counterparty and not a member of staff or a student — the whole
  // point of the epic is that the two never get confused.
  //
  // Both items mirror the BE's read @Auth on the LISTS exactly:
  //   GET /v1/partner-applications  @Auth(ADMIN, SUPER_ADMIN)
  //   GET /v1/admin/partners        @Auth(ADMIN, SUPER_ADMIN)
  //
  // CUSTOMER-CARE IS DELIBERATELY ABSENT. An earlier draft listed it here on
  // the belief that the two lists admitted it; they do not, so the entries
  // would have led to two screens that answer a permission error on load. A nav
  // item for a page the API refuses is worse than no nav item — it reads as an
  // outage rather than as a boundary.
  //
  // The `partner` role itself is NOT here and never will be. Partners do not
  // sign into this app; they hold no admin token and see no nav.
  {
    subheader: 'partners',
    roles: ['admin', 'super-admin'],
    items: [
      {
        title: 'Partners',
        path: PATH_DASHBOARD.partners.list,
        icon: ICONS.invoice,
        roles: ['admin', 'super-admin'],
      },
      {
        title: 'Partner Applications',
        path: PATH_DASHBOARD.partners.applications,
        icon: ICONS.mail,
        roles: ['admin', 'super-admin'],
      },
      // PARTNER PLATFORM S2 — the course review queue.
      //
      // A CHILD OF THE EXISTING `partners` SUBHEADER, not a new section: a
      // partner's course is the same commercial relationship as their
      // application, one step further along, and splitting them would ask an
      // operator to remember which of two sections holds which half of one
      // partner.
      //
      // Mirrors the BE's read @Auth on the list exactly:
      //   GET /v1/admin/partner-courses  @Auth(ADMIN, SUPER_ADMIN)
      //
      // CUSTOMER-CARE STAYS ABSENT, for the reason recorded above this section:
      // it is on none of the partner @Auth lists, so a nav item for it would
      // lead to a screen that answers a permission error on load — which reads
      // as an outage rather than as a boundary.
      {
        title: 'Partner Courses',
        path: PATH_DASHBOARD.partners.courses,
        icon: ICONS.booking,
        roles: ['admin', 'super-admin'],
      },
      // BUNNY EPIC — the partner video operations screen.
      //
      // A CHILD OF THE SAME `partners` SUBHEADER, not a new "infrastructure"
      // section. Every row on that screen is one partner's lesson and every
      // action on it is visible to that partner within seconds — a withdrawn
      // video stops playing for their students, a detached one empties their
      // lesson. It is a partner operation with a provider underneath it, which
      // is the same reasoning that put the course queue here.
      //
      // Mirrors the BE's read @Auth on the status and the list exactly:
      //   GET /v1/admin/bunny/status  @Auth(ADMIN, SUPER_ADMIN)
      //   GET /v1/admin/bunny/videos  @Auth(ADMIN, SUPER_ADMIN)
      //
      // CUSTOMER-CARE STAYS ABSENT, for the reason recorded above this section.
      //
      // The entry is present even with no provider credentials configured, ON
      // PURPOSE: with no key there are no rows, and the screen's first card is
      // the only thing on the platform that says why and names the variable to
      // set. Hiding the nav item until it was configured would hide the
      // instructions for configuring it.
      {
        title: 'Partner Video',
        path: PATH_DASHBOARD.partners.bunny,
        icon: ICONS.analytics,
        roles: ['admin', 'super-admin'],
      },
    ],
  },

  // LEARNING
  // ----------------------------------------------------------------------
  {
    subheader: 'learning',
    // customer-care granted admin-like access to the whole Learning section.
    // 'sales-manager' is added ONLY so the section header renders for them;
    // within it they see just Packages (view-only) — the other items keep
    // their own roles. 'coordinator' is here for the same reason: the group's
    // own `roles` gates the whole section (nav-section/vertical/index.tsx:27),
    // so without it the Batch Management item below is unreachable for them.
    roles: ['admin', 'super-admin', 'customer-care', 'sales-manager', 'coordinator'],
    items: [
      {
        title: 'Courses',
        path: PATH_DASHBOARD.courses.root,
        icon: ICONS.menuItem,
        roles: ['admin', 'super-admin', 'customer-care'],
      },
      {
        title: 'Tracks',
        path: PATH_DASHBOARD.tracks,
        icon: ICONS.kanban,
        roles: ['admin', 'super-admin', 'customer-care'],
      },
      {
        title: 'Diplomas',
        path: PATH_DASHBOARD.diplomas,
        icon: ICONS.booking,
        roles: ['admin', 'super-admin', 'customer-care'],
      },
      {
        title: 'Paths',
        path: PATH_DASHBOARD.categories,
        icon: ICONS.calendar,
        roles: ['admin', 'super-admin', 'customer-care'],
      },
      {
        title: 'Packages',
        path: PATH_DASHBOARD.packages,
        icon: ICONS.booking,
        roles: ['admin', 'super-admin', 'customer-care', 'sales-manager'],
      },
      {
        // E1: coordinator added — the Zoom host tab lives in here and picking
        // the licence a batch runs on is their job, since they own the
        // timetable that decides whether two batches collide.
        title: 'Batch Management',
        path: PATH_DASHBOARD.batchManagement,
        icon: ICONS.calendar,
        roles: ['admin', 'super-admin', 'customer-care', 'coordinator'],
      },
    ],
  },

  // MARKETING: tracked lead forms and their leads (lead-forms SPEC §D.1, §H).
  // The group roles AND each item's roles list the four roles (mirrors BE
  // LEAD_FORM_ROLES / LEAD_INBOX_ROLES); sales and customer-care see neither.
  // ----------------------------------------------------------------------
  {
    subheader: 'marketing',
    roles: ['admin', 'super-admin', 'sales-manager', 'marketing'],
    items: [
      {
        title: 'Lead forms',
        path: PATH_DASHBOARD.leadForms,
        icon: ICONS.analytics,
        roles: ['admin', 'super-admin', 'sales-manager', 'marketing'],
      },
      {
        title: 'Leads',
        path: PATH_DASHBOARD.leads,
        icon: ICONS.mail,
        // navConfig is a static array: the badge fetches its own count.
        info: <LeadsNavBadge />,
        roles: ['admin', 'super-admin', 'sales-manager', 'marketing'],
      },
      {
        // Plan H: show/hide switches for the kayfa.io home page sections.
        // Mirrors BE HOME_SECTION_ROLES; sales-manager does not see it.
        title: 'Home page sections',
        path: PATH_DASHBOARD.homeSections,
        icon: ICONS.dashboard,
        roles: ['admin', 'super-admin', 'marketing'],
      },
    ],
  },

  // CONTENT (ported from live `dev`: blogs + SEO pages)
  // ----------------------------------------------------------------------
  {
    subheader: 'content',
    // 'instructor'/'coordinator' are added ONLY so the section header renders
    // for them; within it they see just the Programs item (its own roles gate
    // the rest). 'sales-manager' likewise sees only Diploma landing pages.
    roles: [
      'admin',
      'super-admin',
      'customer-care',
      'sales',
      'sales-manager',
      'instructor',
      'coordinator',
    ],
    items: [
      {
        title: 'Diploma landing pages',
        path: PATH_DASHBOARD.landingPages,
        icon: ICONS.booking,
        roles: ['admin', 'super-admin', 'customer-care', 'sales', 'sales-manager'],
      },
      {
        title: 'Blogs',
        path: PATH_DASHBOARD.blogs,
        icon: ICONS.blog,
        roles: ['admin', 'super-admin', 'customer-care', 'sales'],
      },
      {
        title: 'Success Stories',
        path: PATH_DASHBOARD.successStories,
        icon: ICONS.blog,
        roles: ['admin', 'super-admin', 'customer-care', 'sales'],
      },
      {
        title: 'Programs',
        path: PATH_DASHBOARD.events,
        icon: ICONS.calendar,
        roles: ['admin', 'super-admin', 'customer-care', 'instructor', 'coordinator'],
      },
      {
        title: 'SEO Pages',
        path: PATH_DASHBOARD.seoPages,
        icon: ICONS.menuItem,
        roles: ['admin', 'super-admin', 'customer-care', 'sales'],
      },
      {
        title: 'Home Statistics',
        path: PATH_DASHBOARD.homeStats,
        icon: ICONS.analytics,
        roles: ['admin', 'super-admin', 'customer-care', 'sales'],
      },
      {
        title: 'About Page',
        path: PATH_DASHBOARD.aboutPage,
        icon: ICONS.menuItem,
        roles: ['admin', 'super-admin', 'customer-care', 'sales'],
      },
      {
        title: 'Company Profile',
        path: PATH_DASHBOARD.companyProfileContent,
        icon: ICONS.menuItem,
        roles: ['admin', 'super-admin', 'customer-care', 'sales'],
      },
      {
        title: 'Notifications',
        path: PATH_DASHBOARD.notifications,
        icon: ICONS.mail,
        roles: ['admin', 'super-admin', 'customer-care', 'sales'],
      },
    ],
  },

  // COMMERCE
  // ----------------------------------------------------------------------
  {
    subheader: 'commerce',
    roles: ['admin', 'super-admin', 'sales', 'sales-manager'],
    items: [
      {
        title: 'Payments',
        path: PATH_DASHBOARD.payments,
        icon: ICONS.banking,
        roles: ['admin', 'super-admin', 'sales-manager'],
      },
      {
        title: 'Promo Code',
        path: PATH_DASHBOARD.promoCode,
        icon: ICONS.ecommerce,
        roles: ['admin', 'super-admin', 'sales', 'sales-manager'],
      },
      {
        title: 'Certificates',
        path: PATH_DASHBOARD.certificates,
        icon: ICONS.invoice,
        roles: ['admin', 'super-admin'],
      },
    ],
  },

  // INSIGHTS
  // ----------------------------------------------------------------------
  {
    subheader: 'insights',
    // 'coordinator' is added ONLY so the section header renders for them; within
    // it they see just Class Reminders (each item's own roles gate the rest).
    roles: [
      'admin',
      'super-admin',
      'sales',
      'sales-manager',
      'customer-care',
      'support',
      'coordinator',
    ],
    items: [
      {
        // Consolidated: Quiz Results, Student Alerts, Instructor Ratings,
        // Course Ratings, Final Projects, Segments — tabs inside one Reports page.
        title: 'Reports',
        path: PATH_DASHBOARD.reports,
        icon: ICONS.analytics,
        roles: ['admin', 'super-admin'],
      },
      {
        // The scoped roles can't open the consolidated Reports page, so the two
        // reports they DO own get their own links to the standalone pages.
        title: 'Segments',
        path: PATH_DASHBOARD.userSegments,
        icon: ICONS.analytics,
        roles: ['sales', 'sales-manager'],
      },
      {
        title: 'Student Alerts',
        path: PATH_DASHBOARD.studentAlerts,
        icon: ICONS.analytics,
        roles: ['customer-care', 'support'],
      },
      {
        // EPIC E7: did the class reminder go out, and if not, why not.
        // coordinator owns the timetable, so they get this even though they see
        // nothing else under Insights.
        title: 'Class Reminders',
        path: PATH_DASHBOARD.reminders,
        icon: ICONS.mail,
        roles: ['admin', 'super-admin', 'customer-care', 'coordinator'],
      },
      {
        title: 'Monitor',
        path: PATH_DASHBOARD.screens,
        icon: ICONS.banking,
        roles: ['admin', 'super-admin'],
      },
    ],
  },

  // ACCOUNT (Phase 2.4: admin self-service)
  // ----------------------------------------------------------------------
  {
    subheader: 'account',
    items: [
      {
        title: 'Security',
        path: PATH_DASHBOARD.account.security,
        icon: ICONS.banking,
      },
      {
        title: 'Active sessions',
        path: PATH_DASHBOARD.account.sessions,
        icon: ICONS.user,
      },
    ],
  },

  // SETTINGS (Phase 3.5: super-admin only; NavSection consumers gate
  // visibility based on the current user's roles — the items here are
  // ALSO RoleBasedGuard-gated at the route level, so a non-super-admin
  // who navigates manually still gets a Permission Denied page)
  // ----------------------------------------------------------------------
  // The GROUP gate is widened to `admin` only so that E6's read-only recording
  // health report below can be reached; every item still carries its own gate
  // and NavSection filters the items individually, so an `admin` sees this
  // group containing Recordings and nothing else.
  {
    subheader: 'settings',
    roles: ['super-admin', 'admin'],
    items: [
      {
        title: 'Admins',
        path: PATH_DASHBOARD.settings.admins,
        icon: ICONS.user,
        roles: ['super-admin'],
      },
      {
        // E1: the only screen where a live Zoom client secret is typed.
        title: 'Zoom accounts',
        path: PATH_DASHBOARD.settings.zoom,
        icon: ICONS.calendar,
        roles: ['super-admin'],
      },
      {
        // 3 Nov 2026: the sales CRM keys are typed here (like the Zoom secrets): super-admin only.
        title: 'Sales CRM',
        path: PATH_DASHBOARD.settings.salesCrm,
        icon: ICONS.user,
        roles: ['super-admin'],
      },
      {
        // E6: what the recording pipeline is configured to do and what is
        // currently stuck. Read-only; no credential and no destructive control.
        title: 'Recordings',
        path: PATH_DASHBOARD.settings.recordings,
        icon: ICONS.analytics,
        roles: ['admin', 'super-admin'],
      },
    ],
  },

  // SUPPORT (Phase 6.11)
  // Impersonation belongs to customer-care only — the `support` role was
  // stripped of it and now works from Students / Messages / Student Alerts.
  // Super-admin inherits via RoleBasedGuard and additionally sees
  // "Active impersonations" — their oversight surface.
  // ----------------------------------------------------------------------
  {
    subheader: 'support',
    roles: ['customer-care', 'super-admin'],
    items: [
      {
        title: 'Impersonate',
        path: PATH_DASHBOARD.support.impersonate,
        icon: ICONS.user,
        roles: ['customer-care'],
      },
      {
        title: 'Active impersonations',
        path: PATH_DASHBOARD.support.impersonationsActive,
        icon: ICONS.banking,
        roles: ['super-admin'],
      },
    ],
  },
];

export default navConfig;
